import {
  Directive,
  Input,
  ElementRef,
  ViewContainerRef,
  HostListener,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ComponentRef,
  Injector
} from '@angular/core';
import {
  Overlay,
  OverlayRef,
  OverlayPositionBuilder,
  ConnectedPosition,
  PositionStrategy
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { TooltipComponent } from '../../components/tooltip/tooltip.component';
import { TooltipService } from '../../services/tooltip.service';
import { Subscription } from 'rxjs';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right' | 'auto';

@Directive({
  selector: '[motnTooltip]',
  standalone: true
})
export class TooltipDirective implements OnDestroy, OnChanges {
  @Input('motnTooltip') content: string | TemplateRef<any> = '';
  @Input() tooltipPosition: TooltipPosition = 'top';
  @Input() tooltipDelay = 200;
  @Input() tooltipShowOnClick = false; // Enable click mode for iPads
  @Input() tooltipClass = '';
  @Input() tooltipDisabled = false;

  private overlayRef?: OverlayRef;
  private tooltipInstance?: ComponentRef<TooltipComponent>;
  private showTimeoutId?: number;
  private hideTimeoutId?: number;
  private isTooltipVisible = false;
  private touchTimeout?: number;
  private tooltipId = Math.random().toString(36).substr(2, 9);
  private closeSubscription?: Subscription;
  private scrollListener?: () => void;
  private mutationObserver?: MutationObserver;
  private cachedScrollContainers?: (Element | Window)[];

  constructor(
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef,
    private viewContainerRef: ViewContainerRef,
    private injector: Injector,
    private tooltipService: TooltipService
  ) {
    // Subscribe to close events from the service
    this.closeSubscription = this.tooltipService.closeTooltip$.subscribe((exceptId: string) => {
      // Close this tooltip if it's not the excepted one
      if (this.isTooltipVisible && this.tooltipId !== exceptId) {
        this.hide(true); // Immediate hide when another tooltip opens
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Close tooltip when content changes (better UX for status updates)
    if (changes['content'] && this.isTooltipVisible) {
      this.hide(true); // Immediate hide when content changes
    }
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (!this.tooltipDisabled && !this.tooltipShowOnClick) {
      this.show();
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (!this.tooltipDisabled && !this.tooltipShowOnClick) {
      // Only hide on mouse leave if not in click mode
      // In click mode, tooltip stays open until clicked again
      this.hide();
    }
  }

  @HostListener('click', ['$event'])
  onClick(event: Event): void {
    if (!this.tooltipDisabled) {
      if (this.tooltipShowOnClick) {
        event.stopPropagation(); // Always stop propagation for tooltip triggers
        event.preventDefault(); // Prevent default click behavior
      }

      if (this.isTooltipVisible) {
        this.hide(true); // Immediate hide on click
      } else if (this.tooltipShowOnClick) {
        // In click mode, explicitly show on click
        // First close any other tooltips
        this.tooltipService.setActiveTooltip(this.tooltipId);
        this.show();
      }
    }
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    if (this.tooltipDisabled || this.tooltipShowOnClick) return;
    this.clearTouchTimeout();
    this.scheduleTouchShow();
  }

  @HostListener('touchend')
  @HostListener('touchcancel')
  onTouchEnd(): void {
    this.clearTouchTimeout();
  }

  private clearTouchTimeout(): void {
    if (this.touchTimeout) {
      clearTimeout(this.touchTimeout);
      this.touchTimeout = undefined;
    }
  }

  private scheduleTouchShow(): void {
    this.touchTimeout = window.setTimeout(() => {
      this.show();
      // Auto-hide after 3 seconds for touch
      this.hideTimeoutId = window.setTimeout(() => {
        this.hide();
      }, 3000);
    }, 200);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // Close tooltip on outside click when in click mode
    if (this.tooltipShowOnClick && this.isTooltipVisible) {
      const clickedInside = this.elementRef.nativeElement.contains(event.target) ||
                          this.overlayRef?.overlayElement.contains(event.target as Node);
      if (!clickedInside) {
        this.hide(true); // Immediate hide when clicking outside
      }
    }
  }

  private show(): void {
    if (this.isTooltipVisible || !this.content) {
      return;
    }

    // Clear any hide timeout
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = undefined;
    }

    // Notify service that this tooltip is becoming active
    this.tooltipService.setActiveTooltip(this.tooltipId);

    // For click mode, show immediately; otherwise use delay
    const delay = this.tooltipShowOnClick ? 0 : this.tooltipDelay;

    if (delay === 0) {
      this.createTooltip();
      this.isTooltipVisible = true;
    } else {
      this.showTimeoutId = window.setTimeout(() => {
        this.createTooltip();
        this.isTooltipVisible = true;
      }, delay);
    }
  }

  private hide(immediate: boolean = false): void {
    // Clear any show timeout
    if (this.showTimeoutId) {
      clearTimeout(this.showTimeoutId);
      this.showTimeoutId = undefined;
    }

    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = undefined;
    }

    // For immediate hide or click mode, hide without delay
    const delay = immediate || this.tooltipShowOnClick ? 0 : 100;

    if (delay === 0) {
      this.destroyTooltip(immediate);
      this.isTooltipVisible = false;
      this.tooltipService.clearActiveTooltip(this.tooltipId);
    } else {
      this.hideTimeoutId = window.setTimeout(() => {
        this.destroyTooltip(false);
        this.isTooltipVisible = false;
        this.tooltipService.clearActiveTooltip(this.tooltipId);
      }, delay);
    }
  }

  private createTooltip(): void {
    // Create overlay with repositioning on DOM changes
    this.overlayRef = this.overlay.create({
      positionStrategy: this.getPositionStrategy(),
      scrollStrategy: this.overlay.scrollStrategies.reposition(), // Reposition instead of close
      hasBackdrop: false,
      panelClass: ['motn-tooltip-panel', this.tooltipClass],
      disposeOnNavigation: true
    });

    // Create component portal and attach
    const portal = new ComponentPortal(TooltipComponent, this.viewContainerRef, this.injector);
    this.tooltipInstance = this.overlayRef.attach(portal);

    // Set content
    if (typeof this.content === 'string') {
      this.tooltipInstance.instance.content = this.content;
    } else {
      this.tooltipInstance.instance.contentTemplate = this.content;
    }

    // Set position class for styling
    this.tooltipInstance.instance.position = this.tooltipPosition;

    // Add manual scroll listener for actual scrolling
    this.addScrollListener();

    // Add mutation observer to detect DOM changes
    this.addMutationObserver();
  }

  private addMutationObserver(): void {
    // Watch for DOM changes that might affect our element
    this.mutationObserver = new MutationObserver((mutations) => {
      // Check if our element is still connected to the DOM
      if (!this.elementRef.nativeElement.isConnected) {
        // Element was removed from DOM, hide tooltip
        if (this.isTooltipVisible) {
          this.hide(true);
        }
        return;
      }

      // For table updates during bulk processing, only update position if tooltip is visible
      // Don't close tooltip for table content changes - only when element is removed
      if (this.overlayRef && this.isTooltipVisible) {
        // Check if the mutation actually affects our specific element
        const affectsOurElement = mutations.some(mutation => {
          return mutation.target === this.elementRef.nativeElement ||
                 this.elementRef.nativeElement.contains(mutation.target as Node) ||
                 (mutation.target as Element).contains?.(this.elementRef.nativeElement);
        });

        if (affectsOurElement) {
          // Only update position, don't close tooltip
          this.overlayRef.updatePosition();
        }
      }
    });

    // Only observe our specific row to avoid closing on other row updates
    const tableRow = this.elementRef.nativeElement.closest('tr');
    const observeTarget = tableRow || this.elementRef.nativeElement;

    this.mutationObserver.observe(observeTarget, {
      childList: true,
      subtree: true,
      attributes: true // Watch attributes for icon changes
    });
  }

  private addScrollListener(): void {
    // Cache containers to avoid repeated DOM queries
    if (!this.cachedScrollContainers) {
      this.cachedScrollContainers = [
        this.elementRef.nativeElement.closest('.table-wrapper'),
        document.querySelector('.dialog-content'),
        window
      ].filter(Boolean) as (Element | Window)[];
    }

    this.scrollListener = () => {
      if (this.isTooltipVisible) {
        this.hide(true);
      }
    };

    // Add listener to all containers
    this.cachedScrollContainers.forEach(container => {
      if (container === window) {
        window.addEventListener('scroll', this.scrollListener!, { passive: true });
      } else {
        (container as Element).addEventListener('scroll', this.scrollListener!, { passive: true });
      }
    });
  }

  private destroyTooltip(immediate: boolean = false): void {
    if (this.overlayRef) {
      if (immediate) {
        // For immediate hide (outside clicks), detach without animation
        this.overlayRef.detach();
      }
      this.overlayRef.dispose();
      this.overlayRef = undefined;
    }
    this.tooltipInstance = undefined;
    this.removeScrollListener();
    this.removeMutationObserver();
  }

  private removeMutationObserver(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = undefined;
    }
  }

  private removeScrollListener(): void {
    if (this.scrollListener && this.cachedScrollContainers) {
      // Use cached containers for removal
      this.cachedScrollContainers.forEach(container => {
        if (container === window) {
          window.removeEventListener('scroll', this.scrollListener!);
        } else {
          (container as Element).removeEventListener('scroll', this.scrollListener!);
        }
      });
      this.scrollListener = undefined;
      this.cachedScrollContainers = undefined;
    }
  }

  private getPositionStrategy(): PositionStrategy {
    const positions = this.getPositions();

    const strategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions(positions)
      .withViewportMargin(8)
      .withFlexibleDimensions(false)
      .withPush(true) // Allow pushing to stay in viewport
      .withGrowAfterOpen(false) // Don't grow after opening
      .withTransformOriginOn('.motn-tooltip');

    return strategy;
  }

  private getPositions(): ConnectedPosition[] {
    const positions: ConnectedPosition[] = [];
    const offset = 8; // Gap between element and tooltip

    switch (this.tooltipPosition) {
      case 'top':
        positions.push({
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: -offset
        });
        // Fallback to bottom if no space
        positions.push({
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          offsetY: offset
        });
        break;

      case 'bottom':
        positions.push({
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          offsetY: offset
        });
        // Fallback to top if no space
        positions.push({
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: -offset
        });
        break;

      case 'left':
        positions.push({
          originX: 'start',
          originY: 'center',
          overlayX: 'end',
          overlayY: 'center',
          offsetX: -offset
        });
        // Fallback to right if no space
        positions.push({
          originX: 'end',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center',
          offsetX: offset
        });
        break;

      case 'right':
        positions.push({
          originX: 'end',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center',
          offsetX: offset
        });
        // Fallback to left if no space
        positions.push({
          originX: 'start',
          originY: 'center',
          overlayX: 'end',
          overlayY: 'center',
          offsetX: -offset
        });
        break;

      case 'auto':
      default:
        // Try positions in order: top, right, bottom, left
        positions.push(
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetY: -offset
          },
          {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
            offsetX: offset
          },
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetY: offset
          },
          {
            originX: 'start',
            originY: 'center',
            overlayX: 'end',
            overlayY: 'center',
            offsetX: -offset
          }
        );
        break;
    }

    return positions;
  }

  ngOnDestroy(): void {
    this.destroyTooltip(true);

    if (this.showTimeoutId) {
      clearTimeout(this.showTimeoutId);
    }
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
    }
    if (this.touchTimeout) {
      clearTimeout(this.touchTimeout);
    }
    if (this.closeSubscription) {
      this.closeSubscription.unsubscribe();
    }
    this.removeMutationObserver();
    this.tooltipService.clearActiveTooltip(this.tooltipId);
  }
}
